/**
 * Workflow Orchestration Page
 *
 * Comprehensive workflow visualization with multiple display modes:
 * - Grid View: 5×5 primitive grid (existing PrimitiveGrid)
 * - Swimlane View: Process flow visualization across lifecycle phases
 * - Flow View: Interactive workflow diagram with node connections
 *
 * FERP Principles:
 * - Data-driven: All workflow data from GraphQL
 * - Safety-critical: Life-critical workflows highlighted
 * - Real-time: WebSocket subscriptions for live updates
 *
 * DRR-072: Meta-orchestration framework visualization
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  SegmentedControl,
  Paper,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconGrid3x3,
  IconLayoutColumns,
  IconGitBranch,
  IconRefresh,
  IconSettings,
} from '@tabler/icons-react';

import { PrimitiveGrid } from '@/components/architect/PrimitiveGrid';
import { WorkflowSwimlane } from './components/WorkflowSwimlane';
import { WorkflowFlowDiagram } from './components/WorkflowFlowDiagram';

type ViewMode = 'grid' | 'swimlane' | 'flow';

const VIEW_MODE_CONFIG = {
  grid: {
    label: 'Grid View',
    icon: IconGrid3x3,
    description: '5×5 primitive grid organized by lifecycle phase',
  },
  swimlane: {
    label: 'Swimlane View',
    icon: IconLayoutColumns,
    description: 'Horizontal process flow across phases',
  },
  flow: {
    label: 'Flow Diagram',
    icon: IconGitBranch,
    description: 'Interactive workflow node graph',
  },
};

export function WorkflowOrchestrationPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" ta="left">
          <div>
            <Group gap="sm" mb="xs">
              <Title order={2}>Workflow Orchestration</Title>
              <Badge variant="light" c="violet" size="lg">
                Meta-Pipeline
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {VIEW_MODE_CONFIG[viewMode].description}
            </Text>
          </div>

          <Group gap="xs">
            <Tooltip label="Refresh Data">
              <ActionIcon variant="light" size="lg" onClick={handleRefresh}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Settings">
              <ActionIcon variant="light" size="lg">
                <IconSettings size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* View Mode Selector */}
        <Paper p="md" withBorder>
          <Group justify="space-between" wrap="wrap">
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              data={[
                {
                  value: 'grid',
                  label: (
                    <Group gap={6}>
                      <IconGrid3x3 size={16} />
                      <span>Grid</span>
                    </Group>
                  ),
                },
                {
                  value: 'swimlane',
                  label: (
                    <Group gap={6}>
                      <IconLayoutColumns size={16} />
                      <span>Swimlane</span>
                    </Group>
                  ),
                },
                {
                  value: 'flow',
                  label: (
                    <Group gap={6}>
                      <IconGitBranch size={16} />
                      <span>Flow</span>
                    </Group>
                  ),
                },
              ]}
            />

            {/* Phase Summary */}
            <Group gap="xs">
              <Badge c="blue" variant="light">5 DESIGN</Badge>
              <Badge c="grape" variant="light">5 CONFIGURE</Badge>
              <Badge c="cyan" variant="light">5 EXECUTE</Badge>
              <Badge c="orange" variant="light">5 MONITOR</Badge>
              <Badge c="red" variant="light">5 OPTIMIZE</Badge>
            </Group>
          </Group>
        </Paper>

        {/* Main Content - Switch based on view mode */}
        {viewMode === 'grid' && <PrimitiveGrid />}
        {viewMode === 'swimlane' && <WorkflowSwimlane />}
        {viewMode === 'flow' && <WorkflowFlowDiagram />}
      </Stack>
    </Container>
  );
}

export default WorkflowOrchestrationPage;
